/**
 * Feature Flags API Client — new admin API (/v1/admin/feature-flags)
 */

import apiClient from './client';
import type {
  FlagsListResponse,
  FlagResponse,
  CreateFlagRequest,
  UpdateFlagRequest,
  TransitionRequest,
  FlagOverride,
  CreateOverrideRequest,
  AuditLogEntry,
} from '@/types/featureFlags';

export const featureFlagsApi = {
  listFlags: async (params?: {
    flag_type?: string;
    lifecycle_state?: string;
    owner?: string;
    search?: string;
  }): Promise<FlagsListResponse> => {
    const response = await apiClient.get('/feature-flags', { params });
    return response.data;
  },

  createFlag: async (data: CreateFlagRequest): Promise<FlagResponse> => {
    const response = await apiClient.post('/feature-flags', data);
    return response.data;
  },

  getFlag: async (flagKey: string): Promise<FlagResponse> => {
    const response = await apiClient.get(`/feature-flags/${encodeURIComponent(flagKey)}`);
    return response.data;
  },

  updateFlag: async (flagKey: string, data: UpdateFlagRequest): Promise<FlagResponse> => {
    const response = await apiClient.put(`/feature-flags/${encodeURIComponent(flagKey)}`, data);
    return response.data;
  },

  transitionFlag: async (flagKey: string, data: TransitionRequest): Promise<FlagResponse> => {
    const response = await apiClient.post(
      `/feature-flags/${encodeURIComponent(flagKey)}/transition`,
      data
    );
    return response.data;
  },

  deleteFlag: async (flagKey: string): Promise<void> => {
    await apiClient.delete(`/feature-flags/${encodeURIComponent(flagKey)}`);
  },

  listOverrides: async (flagKey: string): Promise<FlagOverride[]> => {
    const response = await apiClient.get(
      `/feature-flags/${encodeURIComponent(flagKey)}/overrides`
    );
    return response.data.overrides;
  },

  createOverride: async (flagKey: string, data: CreateOverrideRequest): Promise<{ set: number }> => {
    const response = await apiClient.post(
      `/feature-flags/${encodeURIComponent(flagKey)}/overrides`,
      data
    );
    return response.data;
  },

  deleteOverride: async (flagKey: string, userId: string): Promise<{ deleted: boolean }> => {
    const response = await apiClient.delete(
      `/feature-flags/${encodeURIComponent(flagKey)}/overrides/${encodeURIComponent(userId)}`
    );
    return response.data;
  },

  getAuditLogs: async (flagKey: string, limit = 50, offset = 0): Promise<AuditLogEntry[]> => {
    const response = await apiClient.get(
      `/feature-flags/${encodeURIComponent(flagKey)}/audit`,
      { params: { limit, offset } }
    );
    return response.data.audit;
  },
};
